package com.evoapps.evoshift.domain.explain

import com.evoapps.evoshift.domain.market.payroll.PayLineKind
import com.evoapps.evoshift.domain.model.LabourShare
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlin.math.abs

/*
 * SH-152: "How was this calculated?" Every financial figure on screen can say how it was worked
 * out, with the real numbers. The domain returns the facts: which figure ([Figure]), its terms
 * ([Term]: what each one is, its amount and the quantities behind it) and the result. The screens
 * word them (UiText convention). The calculations are built from the same values the screens
 * show (and the same per-day choices the totals are added up with), so their terms always add
 * up to the figure on screen: a difference too small to see (rounding of stored amounts) or a
 * total saved before later changes gets a term of its own ([Breakdown.of]) instead of a silent gap.
 */

/** What a calculation explains: the sheet's title. */
enum class Figure {
    /** Labour cost as a share of the income (or of the net sales without a commission). */
    LABOUR_PERCENT,
    /** Labour cost as a share of the net sales (shown beside the share of income). */
    LABOUR_PERCENT_OF_SALES,
    LABOUR_COST,
    /** What labour % is measured against: the sales income and the fixed revenue. */
    INCOME,
    /** A day's or a month's income with its revenue items (the result's revenue). */
    REVENUE,
    NET_SALES,
    SALES_TARGET,
    TAKINGS,
    /** The sales income: the commission, or the net sales kept. */
    SALES_INCOME,
    FIXED_REVENUE,
    RECURRING_REVENUE,
    ONE_OFF_REVENUE,
    /** Labour plus the other costs. */
    COSTS,
    OTHER_COSTS,
    FIXED_COSTS,
    ONE_OFF_COSTS,
    SHARED_COSTS,
    RESULT,
    MARGIN,
    // The labour cost screen.
    GROSS,
    TAKE_HOME,
    EMPLOYER_COST,
    /** The shifts' employer cost alone (as on the Finances screen). */
    SHIFTS_EMPLOYER_COST,
    /** Bonuses, supplements and the month's settlement on top of the shifts. */
    MONTH_ITEMS,
    // The roster.
    BUDGET_USED,
    SHIFT_COST,
    /** A day's staffing hint: planned labour against the expected income. */
    STAFFING_LABOUR_PERCENT,
    /** SH-165: how giving away, swapping or taking a shift changes the person's own estimated take-home pay. */
    PAY_CHANGE,
    /** SH-191: sales per man-hour: the net sales ÷ the hours worked. */
    SPMH
}

/** Which side of a figure: the plan, the actual (to date) or the forecast (plan + actual). */
enum class Side { PLAN, ACTUAL, FORECAST }

/** How a term takes part: added, taken away, or listed but not counted (e.g. people without pay set). */
enum class Sign { PLUS, MINUS, NOT_COUNTED }

/** What a term is: the screens name it (with [Term.name] or the person's name where it has one). */
enum class TermKind {
    // Labour.
    /** One person's shifts: [Term.personId], their hours and shifts. */
    PERSON,
    /** A person working without pay terms: their shifts are not in the labour cost. */
    PERSON_WITHOUT_PAY,
    /** A person who works without pay (the owner, a partner): 0 on purpose. */
    PERSON_UNPAID,
    /** SH-206: a person who does not count in the labour cost and SPMH (their switch is off): listed, not counted. */
    PERSON_NOT_COUNTED,
    /** Unassigned shifts at an estimated cost per hour. */
    OPEN_SHIFTS,
    /** Unassigned shifts not priced (no cost per hour known yet). */
    OPEN_SHIFTS_NOT_PRICED,
    /** A day's labour of one contract type ([Term.contract]), as the saved day holds it. */
    CONTRACT_TYPE,
    /** The labour cost (its parts beneath). */
    LABOUR_COST,
    // Sales and income.
    /** The commission: [Term.percent] of [Term.base], the net sales. */
    COMMISSION,
    /** Without a commission the owner keeps the net sales ([Term.base]). */
    NET_SALES_KEPT,
    /** The month's sales income when the commission changed during it (commission + sales kept). */
    SALES_INCOME_MIXED,
    /** The fixed monthly revenue: [Term.days] of [Term.ofDays] days' shares. */
    FIXED_REVENUE,
    /** The sales income and the fixed revenue (its parts beneath). */
    INCOME,
    /** The month's revenue from its recurring rules. */
    RECURRING_REVENUE,
    /** The month's one-off revenue. */
    ONE_OFF_REVENUE,
    /** Revenue items of the ledger dated on the day. */
    REVENUE_ITEMS,
    /** One ledger item ([Term.name]). */
    LEDGER_ITEM,
    /** Takings entered for [Term.days] days. */
    TAKINGS,
    /** The day's takings, typed in the app. */
    TAKINGS_TYPED,
    /** The day's takings, imported from a file. */
    TAKINGS_FILE,
    /** The day's takings, read from the till. */
    TAKINGS_TILL,
    /** Sales targets of [Term.days] days. */
    SALES_TARGETS,
    /** The day's sales target. */
    SALES_TARGET,
    // Costs and result.
    FIXED_COSTS,
    ONE_OFF_COSTS,
    SHARED_COSTS,
    /** Costs of the ledger dated on the day. */
    OTHER_COSTS,
    /** Revenue (its parts beneath). */
    REVENUE,
    /** Costs (their parts beneath). */
    COSTS,
    // A person's month (the labour cost screen).
    /** Pay for the hours (or the salary): [Term.hours]. */
    BASE_PAY,
    /** A salary's normal pay for the hours over the period's norm. */
    OVERTIME_PAY,
    OVERTIME_50,
    OVERTIME_100,
    SUNDAY_HOLIDAY,
    MINIMUM_WAGE_TOP_UP,
    SICK_PAY,
    SICK_BENEFIT,
    HOLIDAY_PAY,
    /** A salary is not paid for days of illness. */
    SALARY_REDUCTION,
    /** Pay terms changed during the month (pro rata). */
    RATE_CHANGE,
    BONUSES,
    GROSS,
    /** Deducted from the gross: [Term.payLine]. */
    EMPLOYEE_LINE,
    /** Paid by the employer on top: [Term.payLine]. */
    EMPLOYER_LINE,
    /** The part of the gross ZUS pays (the sickness benefit): not the employer's cost. */
    PAID_BY_ZUS,
    DEDUCTIONS,
    EXPENSE_REFUNDS,
    SHIFTS_EMPLOYER_COST,
    MONTH_ITEMS,
    /** SH-165: what the taxes and contributions take of a change of the gross (the take-home pay's difference). */
    TAXES_AND_CONTRIBUTIONS,
    /** SH-165: what the month's supplements (overtime, Sundays and holidays, the minimum wage) gain or lose with a shift. */
    SUPPLEMENTS_CHANGE,
    // A shift.
    /** The shift's cost as saved in its cost snapshot (older snapshots without the parts). */
    SHIFT_SNAPSHOT,
    /** The shift's gross pay from its cost snapshot: [Term.hours] × [Term.rate]. */
    SHIFT_GROSS,
    NIGHT_ALLOWANCE,
    EMPLOYER_CONTRIBUTIONS,
    /** [Term.hours] at the person's own cost per hour (their shifts' snapshots this month). */
    HOURS_AT_OWN_RATE,
    /** [Term.hours] at the month's average employer cost per hour. */
    HOURS_AT_MONTH_AVERAGE,
    /** [Term.hours] at the team's average cost per hour from their pay terms. */
    HOURS_AT_TEAM_AVERAGE,
    /** The staffing hint's planned shifts. */
    PLANNED_SHIFTS,
    /** The day's expected sales: [Term.base]. */
    EXPECTED_SALES,
    // Corrections, so the terms always add up to the figure.
    /** Rounding of the amounts to the grosz/cent. */
    ROUNDING,
    /** The figure as saved before later changes (it is recalculated when the month is saved again). */
    SAVED_EARLIER
}

/**
 * One term of a calculation: what it is ([kind]), its [amount] and [sign], and what it is made of
 * where that is known ([hours] × [rate], [percent] of [base], [days] of [ofDays], [shifts]), with
 * its own parts beneath ([parts] add up to [amount]).
 */
data class Term(
    val kind: TermKind,
    val amount: Double,
    val sign: Sign = Sign.PLUS,
    /** The person the term is about (the screens show their name). */
    val personId: String? = null,
    /** A ledger item's own name. */
    val name: String? = null,
    val hours: Double? = null,
    val shifts: Int? = null,
    /** Per hour. */
    val rate: Double? = null,
    /** In percent (25.2 = 25.2%). */
    val percent: Double? = null,
    /** What [percent] is of. */
    val base: Double? = null,
    val days: Int? = null,
    val ofDays: Int? = null,
    val payLine: PayLineKind? = null,
    val contract: SystemContractType? = null,
    val parts: List<Term> = emptyList()
) {
    /** What the term adds to its calculation: + [amount], − [amount], or nothing when not counted. */
    val signedAmount: Double
        get() = when (sign) {
            Sign.PLUS -> amount
            Sign.MINUS -> -amount
            Sign.NOT_COUNTED -> 0.0
        }

    val isCounted: Boolean get() = sign != Sign.NOT_COUNTED
}

/** A figure and how it was worked out. */
sealed interface Calculation {
    val figure: Figure
    val side: Side?
    /** The figure itself; null where there is none (a share without a base). */
    val value: Double?
}

/**
 * A figure that is a sum: its [terms] (signed) add up to [value], the figure on screen, to the
 * grosz ([Breakdown.of] adds a term of its own for any visible difference).
 */
data class Breakdown(
    override val figure: Figure,
    override val side: Side?,
    val terms: List<Term>,
    /** The figure as on screen; the sum of [terms] unless given. */
    private val total: Double = terms.sumOf { it.signedAmount }
) : Calculation {
    override val value: Double get() = total

    /** What the terms add up to (equal to [value] within [MONEY_EPSILON]). */
    val termsSum: Double get() = terms.sumOf { it.signedAmount }

    /** As a term of another calculation: [kind] with these terms beneath. */
    fun asTerm(kind: TermKind, sign: Sign = Sign.PLUS): Term = Term(kind, value, sign, parts = terms)

    companion object {
        /** Half a grosz: what money on screen (two decimals) cannot show. */
        const val MONEY_EPSILON = 0.005

        /**
         * [terms] adding up to [shown], the figure on screen: when they do not (rounding, or a
         * total saved before later changes), the difference is a term of its own, [residual].
         * Terms of nothing (0, without hours or parts) are left out.
         */
        fun of(figure: Figure, side: Side?, shown: Double, terms: List<Term>, residual: TermKind = TermKind.ROUNDING): Breakdown {
            val kept = terms.filter { it.sign == Sign.NOT_COUNTED || abs(it.amount) >= MONEY_EPSILON || it.parts.isNotEmpty() || it.hours != null }
            val difference = shown - kept.sumOf { it.signedAmount }
            val all = if (abs(difference) >= MONEY_EPSILON) {
                kept + Term(residual, abs(difference), if (difference < 0) Sign.MINUS else Sign.PLUS)
            } else kept
            return Breakdown(figure, side, all, shown)
        }
    }
}

/** A figure that is a share in percent: [numerator] ÷ [denominator] × 100. */
data class Ratio(
    override val figure: Figure,
    override val side: Side?,
    val numerator: Breakdown,
    val denominator: Breakdown
) : Calculation {
    override val value: Double? get() = LabourShare.percent(numerator.value, denominator.value)
}

/**
 * SH-191: a figure per hour worked ([Figure.SPMH]): the net [sales] ÷ [hours]. [plannedHours]
 * says the hours are the planned ones (none counted as worked yet).
 */
data class PerHour(
    override val figure: Figure,
    override val side: Side?,
    val sales: Breakdown,
    val hours: Double,
    val plannedHours: Boolean = false
) : Calculation {
    override val value: Double? get() = com.evoapps.evoshift.domain.model.Spmh.of(sales.value, hours)
}
